/**
 * Traer aquí lo que ya es tuyo.
 *
 * Contesta dos preguntas que el producto no sabía contestar, y las dos llegan por email antes de
 * que las conteste el panel: «me habéis regalado un año, ¿cómo lo cojo?» y «me he cambiado de
 * portátil». Hasta ahora la suscripción vivía atada al identificador de una instalación —un número
 * aleatorio nacido en ese ordenador—, así que cambiar de máquina era perderlo.
 *
 * NO es un registro. No hay cuenta, ni contraseña nuestra, ni base de datos de escritores: solo un
 * email y un código de un solo uso que vale quince minutos. Lo que se guarda es a qué instalación
 * apunta ese email, y nada más.
 *
 * Y el mensaje del primer paso es deliberadamente vago —«si ese email tiene algo a su nombre»—
 * porque decir «ese email no consta» convertiría esta caja en un buscador del censo del Club.
 */
import { useState } from "react";
import { reclamar, canjear } from "../api";

export default function Traer({ alEntrar }: { alEntrar: () => void }) {
  const [paso, setPaso] = useState<"cerrado" | "email" | "codigo" | "hecho">("cerrado");
  const [email, setEmail] = useState("");
  const [codigo, setCodigo] = useState("");
  const [aviso, setAviso] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [movida, setMovida] = useState(false);

  async function pedir() {
    if (!email.trim() || ocupado) return;
    setOcupado(true); setFallo(null);
    try {
      const r = await reclamar(email);
      setAviso(r.mensaje ?? "Si ese email tiene algo a su nombre, le llega un código.");
      setPaso("codigo");
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se ha podido pedir el código.");
    } finally { setOcupado(false); }
  }

  async function usar() {
    if (codigo.trim().length < 4 || ocupado) return;
    setOcupado(true); setFallo(null);
    try {
      const r = await canjear(email, codigo);
      // Un error sin traducir se enseña con su código: «El código no ha valido» a secas no le dice
      // nada al autor ni a soporte (César, 15-sep: «dice que está deshabilitado» y no había forma de saber qué).
      if (!r.ok || r.error) {
        setFallo(MOTIVOS[r.error ?? ""] ?? `El código no ha valido (${r.error ?? "sin detalle"}). Escríbenos a info@humanink.io con este mensaje.`);
        return;
      }
      setMovida(!!r.movida);
      setPaso("hecho");
      alEntrar();
    } catch (e) {
      setFallo(e instanceof Error ? e.message : "No se ha podido canjear el código.");
    } finally { setOcupado(false); }
  }

  if (paso === "hecho") {
    return (
      <p className="vacio">
        ✓ Listo, ya está activo en este ordenador.
        {movida && " Lo he traído desde el equipo anterior, que a partir de ahora te lo pedirá a ti."}
      </p>
    );
  }

  if (paso === "cerrado") {
    return (
      <p className="vacio">
        ¿Te hemos regalado un año, o ya pagaste en otro ordenador?{" "}
        <button className="chip" onClick={() => setPaso("email")}>Tráelo aquí</button>
      </p>
    );
  }

  return (
    <>
      {paso === "email" ? (
        <>
          <p className="vacio">Escribe el email con el que compraste o con el que estás en el Club.</p>
          <div className="dir">
            <input
              className="escribir" type="email" autoFocus placeholder="tu@email.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") pedir(); }}
            />
            <button className="btn" onClick={pedir} disabled={ocupado || !email.trim()}>
              {ocupado ? "Enviando…" : "Mandarme el código"}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="vacio">{aviso} Caduca en quince minutos.</p>
          <div className="dir">
            <input
              className="escribir mono" autoFocus inputMode="numeric" placeholder="6 cifras"
              value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={(e) => { if (e.key === "Enter") usar(); }}
            />
            <button className="btn" onClick={usar} disabled={ocupado || codigo.length < 4}>
              {ocupado ? "Comprobando…" : "Activar aquí"}
            </button>
          </div>
          {/* El código caduca en quince minutos y antes no había forma de pedir otro sin recargar la página. */}
          <p className="vacio">
            ¿No te ha llegado o ya caducó?{" "}
            <button className="chip" onClick={() => { setCodigo(""); setFallo(null); setPaso("email"); }}>
              Pedir otro código
            </button>
          </p>
        </>
      )}
      {fallo && <p className="vacio" style={{ color: "var(--acc)" }}>{fallo}</p>}
    </>
  );
}

/** Los errores del servidor, dichos como se los diría alguien a un amigo. */
const MOTIVOS: Record<string, string> = {
  codigo_caducado: "Ese código ya no vale — caduca a los quince minutos y solo sirve una vez. Pide otro.",
  codigo_incorrecto: "Ese código no es. Míralo otra vez en el correo.",
  demasiados_intentos: "Demasiados intentos. Espera un momento y pide un código nuevo.",
  sin_derecho: "Ese email no tiene nada a su nombre todavía. Si crees que sí, escribe a info@humanink.io.",
  faltan_datos: "Faltan datos. Vuelve a intentarlo.",
  sin_red: "No hay conexión con HumanInk. Prueba otra vez en un momento.",
};
